import { useGetReview } from '@/common/apis/services/reviews/getReviews';
import { UserInfo } from '@/modules/login/store/userInfo';
import { useMemo } from 'react';
import { useAnsweredFeedbackIds } from '../apis/reviewInteractions';
import { useSelectedCenterStore } from '../store/selectedCenter';
import { useSelectedDateStore } from '../store/selectedDate';
import { DoctorHomeFeedReview } from '../types/feed';
import { filterReviewsBySelectedCenter, filterReviewsBySelectedDate, mapRawReviewToFeed } from '../utils/normalizeReview';
import { doctorHomeListQueryDefaults } from '../utils/doctorHomeQuery';
import { extractFeedbackList } from '../utils/reviewWeekDelta';
import { useDoctorHomeContext } from './useDoctorHomeContext';

const MAX_FEED_REVIEWS = 5;
/** چند نظرِ اولِ روز که پاسخ‌داشتنشان چک می‌شود (هر کدام یک درخواست replies) */
const MAX_REPLY_CHECKS = MAX_FEED_REVIEWS * 2;
/** سقف limit سرویس ravi-apis — بیشتر از این BAD_REQUEST می‌دهد */
const RAVI_APIS_MAX_LIMIT = 100;

export const useDoctorHomeReviewsFeed = (user?: UserInfo) => {
  const { isDoctor, slug } = useDoctorHomeContext(user);
  const selectedDate = useSelectedDateStore(state => state.selectedDate);
  const selectedCenterId = useSelectedCenterStore(state => state.selectedCenterId);

  // فیلتر تاریخ سمت کلاینت: مسیر NocoDB (فیلتر created_at سمت سرور) روی ravi-api حذف شده
  // (route not found)؛ سرویس ravi-apis جدیدترین‌ها را می‌دهد و روز انتخابی همین‌جا جدا می‌شود.
  const reviews = useGetReview(
    {
      slug,
      sort: 'created_at',
      offset: 0,
      limit: RAVI_APIS_MAX_LIMIT,
      ...(selectedCenterId ? { center_id: selectedCenterId } : {}),
    },
    {
      enabled: isDoctor && !!slug,
      ...doctorHomeListQueryDefaults,
    },
  );

  const dayReviews = useMemo(() => {
    if (!isDoctor || !reviews.data) return [] as DoctorHomeFeedReview[];

    return filterReviewsBySelectedDate(
      filterReviewsBySelectedCenter(extractFeedbackList(reviews.data), selectedCenterId),
      selectedDate,
    ).map(item => mapRawReviewToFeed(item, slug, { selectedDate }));
  }, [isDoctor, reviews.data, selectedCenterId, selectedDate, slug]);

  const replyCheckIds = useMemo(
    () =>
      dayReviews
        .slice(0, MAX_REPLY_CHECKS)
        .map(review => review.id)
        .filter((id): id is NonNullable<typeof id> => id != null)
        .map(String),
    [dayReviews],
  );
  const answeredIds = useAnsweredFeedbackIds(slug, replyCheckIds);

  const items = useMemo(
    () =>
      dayReviews
        .filter(review => review.id == null || !answeredIds.has(String(review.id)))
        .slice(0, MAX_FEED_REVIEWS),
    [dayReviews, answeredIds],
  );

  return {
    isDoctor,
    slug,
    items,
    isInitialLoading: reviews.isLoading && reviews.data == null,
    doctorUserId: user?.id != null ? String(user.id) : undefined,
  };
};
